import React, {useCallback, useEffect, useState} from "react";
import {connect, useSelector} from "react-redux";
import {RouteComponentProps} from "react-router";
import {css, Theme} from "@emotion/react";

import {Button} from "@pg-design/button-module";
import {Hidden} from "@pg-design/grid-module";
import {p} from "@pg-design/helpers-css";
import {HorizontalListIcon} from "@pg-design/icons-module";
import {useIsMounted} from "@pg-mono/hooks";
import {parseSearch} from "@pg-mono/request";
import {navigationHeight} from "@pg-rp/navigation";

import {IRPStore} from "../../app/rp_reducer";
import {MobileOptionsModal} from "../../atoms/MobileOptionsModal";
import {LeadModal} from "../../lead/components/lead_modal/LeadModal";
import {MapOfferPreview} from "../../offer/components/map_offer/MapOfferPreview";
import {OfferListMap} from "../../offer/components/OfferListMap";
import {useOfferListMapTransportRoutes} from "../../offer/hooks/use_offer_list_map_transport_routes";
import {gtmEventMapList, MapGTMActions, MapGTMLabel} from "../../offer/map/utils/map_analytics";
import {mapHolder, MapSize} from "../../offer/views/OfferListMapView";
import {DesktopBlend} from "../../search/components/atoms/atoms";
import {searchFormWithDrawerHeight} from "../../search/constants/search_form_with_drawer_height";
import {sidebarCloseHit} from "../../tracking/algolytics_hits/map_interaction_hits";
import {ViewType} from "../../view_type/ViewType";
import {InvestmentOfferHeader} from "../atoms/InvestmentOfferHeader";
import {useInvestmentOfferFilters} from "../hooks/use_investment_offer_filters";
import {useInvestmentOfferViewsUrl} from "../hooks/use_investment_offer_views_url";
import {IParsedInvestmentOfferCategories} from "../list/actions/parse_investment_offer_url";
import {InvestmentOfferListDesktopFilters} from "../list/components/InvestmentOfferListDesktopFilters";
import {InvestmentOfferListMobileFilters} from "../list/components/InvestmentOfferListMobileFilters";
import {IInvestmentOffersOffersStore} from "../list/reducers/investment_offer_list_reducer";

interface IStateProps {
    offers: IInvestmentOffersOffersStore;
    categories: IParsedInvestmentOfferCategories;
    pageTitle: string;
    propertiesCount: number | null;
}

interface IOwnProps extends RouteComponentProps<{}> {}
interface IProps extends IStateProps, IOwnProps {}

const InvestmentOfferListMapViewC = (props: IProps) => {
    const isMounted = useIsMounted();

    const [isMobileFiltersActive, setMobileFiltersActive] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [offerId, setOfferId] = useState<number | null>(null);
    const [isInvestmentOffersBlendActive, setInvestmentOffersBlendActive] = useState(false);

    const [windowHeight, setWindowHeight] = useState(0);

    const {transportRoutes} = useOfferListMapTransportRoutes();
    const {listUrl} = useInvestmentOfferViewsUrl();
    const {formValues: filtersFormValues, onChange: onFiltersChange, onSave: onFiltersSave, onStoreFormValues, resetFilters} = useInvestmentOfferFilters();
    const query = parseSearch(props.location.search);
    const type = typeof query.type === "string" ? parseInt(query.type, 10) : undefined;
    // map will flicker without useCallback
    const onMarkerClick = useCallback((offerId: number) => {
        setIsSidebarOpen(true);
        setOfferId(offerId);
    }, []);
    const latestQuery = useSelector((store: IRPStore) => store.offerListMap.list.latestQuery);

    useEffect(() => {
        setWindowHeight(document.body.clientHeight);
    }, [isInvestmentOffersBlendActive]);

    useEffect(() => {
        onStoreFormValues(isMobileFiltersActive ? filtersFormValues : null);
    }, [isMobileFiltersActive]);

    const onOfferSidebarClose = () => {
        setIsSidebarOpen(false);
        gtmEventMapList(MapGTMActions.SIDEBAR, MapGTMLabel.CLOSE);
        sidebarCloseHit(ViewType.OFFER_LIST_MAP);
        setOfferId(null);
    };

    const handleSetOfferId = (offerId: number | null) => {
        setOfferId(offerId);
    };

    return (
        <div css={investmentOfferListMapView}>
            <div css={searchWrapper}>
                <DesktopBlend
                    isActive={isInvestmentOffersBlendActive}
                    pageHeight={`${windowHeight}px`}
                    top={`${searchFormWithDrawerHeight}px`}
                    heightDecrease={`${navigationHeight + searchFormWithDrawerHeight}px`}
                />
                <InvestmentOfferListDesktopFilters
                    onChange={(newFiltersValue) => onFiltersChange(newFiltersValue, true)}
                    setInvestmentOffersBlendActive={setInvestmentOffersBlendActive}
                    values={filtersFormValues}
                    isMap
                    listUrl={listUrl}
                />
            </div>

            <div css={outerWrapper}>
                {!isSidebarOpen && (
                    <InvestmentOfferHeader
                        categories={props.categories}
                        css={header}
                        pageTitle={props.pageTitle}
                        propertiesCount={props.propertiesCount}
                        offerCount={props.offers.count}
                        region={props.categories.subcategory}
                        isMap
                        offerType={type}
                        page={props.offers.page}
                    />
                )}

                <div css={[mapHolder, mapStyle]}>
                    <Hidden css={listButtonPositioner} on="mobile">
                        <Button href={listUrl} css={listButton} iconLeft={HorizontalListIcon} variant="filled_primary">
                            Lista
                        </Button>
                    </Hidden>

                    {isMounted && (
                        <MapSize fullWidth={!isSidebarOpen}>
                            <OfferListMap
                                transportRoutes={transportRoutes}
                                onMarkerClick={onMarkerClick}
                                offers={props.offers.offerList}
                                offersRequestState={props.offers.requestState}
                                isHoliday
                            />
                        </MapSize>
                    )}

                    {offerId && (
                        <MapOfferPreview
                            offerId={offerId}
                            handleSetOfferId={handleSetOfferId}
                            onClose={onOfferSidebarClose}
                            latestQuery={latestQuery}
                            isOpen={isSidebarOpen}
                        />
                    )}
                </div>

                <MobileOptionsModal modalHeading="Filtruj" isOpen={isMobileFiltersActive} onModalClose={() => setMobileFiltersActive(false)}>
                    <InvestmentOfferListMobileFilters
                        onChange={(newFiltersValue) => onFiltersChange(newFiltersValue, false)}
                        onSave={() => {
                            onFiltersSave();
                            setMobileFiltersActive(false);
                        }}
                        onReset={resetFilters}
                        values={filtersFormValues}
                    />
                </MobileOptionsModal>
                <LeadModal />
            </div>
        </div>
    );
};

export const InvestmentOfferListMapView = connect(mapStateToProps)(InvestmentOfferListMapViewC);

function mapStateToProps(state: IRPStore): IStateProps {
    return {
        offers: state.investmentOffer.list.offers,
        categories: state.investmentOffer.categories,
        pageTitle: state.metaData.standardMetaData.title,
        propertiesCount: state.investmentOffer.list.propertiesCount
    };
}

const investmentOfferListMapView = css`
    height: calc(100vh - 6rem);
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const searchWrapper = (theme: Theme) => css`
    display: block;
    width: 100%;
    height: auto;
    padding: 0 1rem;

    @media (min-width: ${theme.breakpoints.md}) {
        background-color: initial;
    }
`;

const outerWrapper = () => css`
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: column;
    flex: 0 1 100%;
    height: 100%;
`;

const mapStyle = css`
    width: 100%;
    flex: 1 1 100%;
`;

const header = (theme: Theme) => css`
    position: absolute;
    top: 0;
    right: 0;
    z-index: 5;
    flex: 0 0 auto;
    max-width: 575px;
    background-color: ${theme.colors.gray[100]};
    ${p(2, 3, 3)};
`;

const listButtonPositioner = css`
    position: absolute;
    top: 2.4rem;
    left: 2.4rem;
    z-index: 5;
`;

const listButton = css`
    min-width: 146px;
`;
import React, {useCallback, useEffect, useState} from "react";
import {connect, useSelector} from "react-redux";
import {RouteComponentProps} from "react-router";
import {css, Theme} from "@emotion/react";

import {Button} from "@pg-design/button-module";
import {Hidden} from "@pg-design/grid-module";
import {p} from "@pg-design/helpers-css";
import {HorizontalListIcon} from "@pg-design/icons-module";
import {useIsMounted} from "@pg-mono/hooks";
import {parseSearch} from "@pg-mono/request";
import {navigationHeight} from "@pg-rp/navigation";

import {IRPStore} from "../../app/rp_reducer";
import {MobileOptionsModal} from "../../atoms/MobileOptionsModal";
import {LeadModal} from "../../lead/components/lead_modal/LeadModal";
import {MapOfferPreview} from "../../offer/components/map_offer/MapOfferPreview";
import {OfferListMap} from "../../offer/components/OfferListMap";
import {useOfferListMapTransportRoutes} from "../../offer/hooks/use_offer_list_map_transport_routes";
import {gtmEventMapList, MapGTMActions, MapGTMLabel} from "../../offer/map/utils/map_analytics";
import {mapHolder, MapSize} from "../../offer/views/OfferListMapView";
import {DesktopBlend} from "../../search/components/atoms/atoms";
import {searchFormWithDrawerHeight} from "../../search/constants/search_form_with_drawer_height";
import {sidebarCloseHit} from "../../tracking/algolytics_hits/map_interaction_hits";
import {ViewType} from "../../view_type/ViewType";
import {InvestmentOfferHeader} from "../atoms/InvestmentOfferHeader";
import {useInvestmentOfferFilters} from "../hooks/use_investment_offer_filters";
import {useInvestmentOfferViewsUrl} from "../hooks/use_investment_offer_views_url";
import {IParsedInvestmentOfferCategories} from "../list/actions/parse_investment_offer_url";
import {InvestmentOfferListDesktopFilters} from "../list/components/InvestmentOfferListDesktopFilters";
import {InvestmentOfferListMobileFilters} from "../list/components/InvestmentOfferListMobileFilters";
import {IInvestmentOffersOffersStore} from "../list/reducers/investment_offer_list_reducer";

interface IStateProps {
    offers: IInvestmentOffersOffersStore;
    categories: IParsedInvestmentOfferCategories;
    pageTitle: string;
    propertiesCount: number | null;
}

interface IOwnProps extends RouteComponentProps<{}> {}
interface IProps extends IStateProps, IOwnProps {}

const InvestmentOfferListMapViewC = (props: IProps) => {
    const isMounted = useIsMounted();

    const [isMobileFiltersActive, setMobileFiltersActive] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [offerId, setOfferId] = useState<number | null>(null);
    const [isInvestmentOffersBlendActive, setInvestmentOffersBlendActive] = useState(false);

    const [windowHeight, setWindowHeight] = useState(0);

    const {transportRoutes} = useOfferListMapTransportRoutes();
    const {listUrl} = useInvestmentOfferViewsUrl();
    const {formValues: filtersFormValues, onChange: onFiltersChange, onSave: onFiltersSave, onStoreFormValues, resetFilters} = useInvestmentOfferFilters();
    const query = parseSearch(props.location.search);
    const type = typeof query.type === "string" ? parseInt(query.type, 10) : undefined;
    // map will flicker without useCallback
    const onMarkerClick = useCallback((offerId: number) => {
        setIsSidebarOpen(true);
        setOfferId(offerId);
    }, []);
    const latestQuery = useSelector((store: IRPStore) => store.offerListMap.list.latestQuery);

    useEffect(() => {
        setWindowHeight(document.body.clientHeight);
    }, [isInvestmentOffersBlendActive]);

    useEffect(() => {
        onStoreFormValues(isMobileFiltersActive ? filtersFormValues : null);
    }, [isMobileFiltersActive]);

    const onOfferSidebarClose = () => {
        setIsSidebarOpen(false);
        gtmEventMapList(MapGTMActions.SIDEBAR, MapGTMLabel.CLOSE);
        sidebarCloseHit(ViewType.OFFER_LIST_MAP);
        setOfferId(null);
    };

    const handleSetOfferId = (offerId: number | null) => {
        setOfferId(offerId);
    };

    return (
        <div css={investmentOfferListMapView}>
            <div css={searchWrapper}>
                <DesktopBlend
                    isActive={isInvestmentOffersBlendActive}
                    pageHeight={`${windowHeight}px`}
                    top={`${searchFormWithDrawerHeight}px`}
                    heightDecrease={`${navigationHeight + searchFormWithDrawerHeight}px`}
                />
                <InvestmentOfferListDesktopFilters
                    onChange={(newFiltersValue) => onFiltersChange(newFiltersValue, true)}
                    setInvestmentOffersBlendActive={setInvestmentOffersBlendActive}
                    values={filtersFormValues}
                    isMap
                    listUrl={listUrl}
                />
            </div>

            <div css={outerWrapper}>
                {!isSidebarOpen && (
                    <InvestmentOfferHeader
                        categories={props.categories}
                        css={header}
                        pageTitle={props.pageTitle}
                        propertiesCount={props.propertiesCount}
                        offerCount={props.offers.count}
                        region={props.categories.subcategory}
                        isMap
                        offerType={type}
                        page={props.offers.page}
                    />
                )}

                <div css={[mapHolder, mapStyle]}>
                    <Hidden css={listButtonPositioner} on="mobile">
                        <Button href={listUrl} css={listButton} iconLeft={HorizontalListIcon} variant="filled_primary">
                            Lista
                        </Button>
                    </Hidden>

                    {isMounted && (
                        <MapSize fullWidth={!isSidebarOpen}>
                            <OfferListMap
                                transportRoutes={transportRoutes}
                                onMarkerClick={onMarkerClick}
                                offers={props.offers.offerList}
                                offersRequestState={props.offers.requestState}
                                isHoliday
                            />
                        </MapSize>
                    )}

                    {offerId && (
                        <MapOfferPreview
                            offerId={offerId}
                            handleSetOfferId={handleSetOfferId}
                            onClose={onOfferSidebarClose}
                            latestQuery={latestQuery}
                            isOpen={isSidebarOpen}
                        />
                    )}
                </div>

                <MobileOptionsModal modalHeading="Filtruj" isOpen={isMobileFiltersActive} onModalClose={() => setMobileFiltersActive(false)}>
                    <InvestmentOfferListMobileFilters
                        onChange={(newFiltersValue) => onFiltersChange(newFiltersValue, false)}
                        onSave={() => {
                            onFiltersSave();
                            setMobileFiltersActive(false);
                        }}
                        onReset={resetFilters}
                        values={filtersFormValues}
                    />
                </MobileOptionsModal>
                <LeadModal />
            </div>
        </div>
    );
};

export const InvestmentOfferListMapView = connect(mapStateToProps)(InvestmentOfferListMapViewC);

function mapStateToProps(state: IRPStore): IStateProps {
    return {
        offers: state.investmentOffer.list.offers,
        categories: state.investmentOffer.categories,
        pageTitle: state.metaData.standardMetaData.title,
        propertiesCount: state.investmentOffer.list.propertiesCount
    };
}

const investmentOfferListMapView = css`
    height: calc(100vh - 6rem);
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const searchWrapper = (theme: Theme) => css`
    display: block;
    width: 100%;
    height: auto;
    padding: 0 1rem;

    @media (min-width: ${theme.breakpoints.md}) {
        background-color: initial;
    }
`;

const outerWrapper = () => css`
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: column;
    flex: 0 1 100%;
    height: 100%;
`;

const mapStyle = css`
    width: 100%;
    flex: 1 1 100%;
`;

const header = (theme: Theme) => css`
    position: absolute;
    top: 0;
    right: 0;
    z-index: 5;
    flex: 0 0 auto;
    max-width: 575px;
    background-color: ${theme.colors.gray[100]};
    ${p(2, 3, 3)};
`;

const listButtonPositioner = css`
    position: absolute;
    top: 2.4rem;
    left: 2.4rem;
    z-index: 5;
`;

const listButton = css`
    min-width: 146px;
`;
